/**
 * Teams API for Falcon
 *
 * Calls Falcon backend which proxies to auth-service for team management.
 */

import apiClient from './client'

export type TeamRole = 'owner' | 'admin' | 'member' | 'viewer'

export interface Team {
  id: string
  name: string
  logo_url?: string | null
  created_at: string
  updated_at: string
}

export interface TeamWithRole {
  id: string
  name: string
  logo_url?: string | null
  role: TeamRole
  joined_at: string | null
  created_at: string
  updated_at: string
}

export interface UserTeamsResponse {
  teams: TeamWithRole[]
}

export interface TeamMember {
  id: string
  user_id: string
  email: string
  name: string
  avatar_url: string | null
  role: TeamRole
  joined_at: string
  last_seen_at: string | null
}

export interface TeamMembersResponse {
  members: TeamMember[]
}

export interface TeamInvite {
  id: string
  team_id: string
  email: string
  role: TeamRole
  invited_by: string
  expires_at: string
  created_at: string
}

export interface TeamInvitesResponse {
  invites: TeamInvite[]
}

export interface CreateTeamRequest {
  name: string
}

export interface UpdateTeamRequest {
  name: string
}

export interface CreateInviteRequest {
  email: string
  role: TeamRole
}

export interface UpdateMemberRoleRequest {
  role: TeamRole
}

export interface TransferOwnershipRequest {
  new_owner_id: string
}

export interface TransferOwnershipResponse {
  message: string
  token: string
  expires_at: string
}

export interface PendingTransferResponse {
  team_id: string
  to_user_id: string
  to_user_name: string
  to_user_email: string
  token: string
  expires_at: string
  created_at: string
}

export interface MessageResponse {
  message: string
}

export interface InviteDetails {
  team_name: string
  invited_by_name: string
  role: TeamRole
  email: string
  expires_at: string
  user_exists: boolean
}

export interface AcceptTransferResponse {
  team_id: string
  team_name: string
  message: string
}

export const teamsApi = {
  // Get all user's teams
  getUserTeams: async (): Promise<UserTeamsResponse> => {
    const response = await apiClient.get<UserTeamsResponse>('/user/teams')
    return response.data
  },

  // Create a new team
  createTeam: async (data: CreateTeamRequest): Promise<Team> => {
    const response = await apiClient.post<Team>('/teams', data)
    return response.data
  },

  // Get team details
  getTeam: async (teamId: string): Promise<Team> => {
    const response = await apiClient.get<Team>(`/t/${teamId}/settings`)
    return response.data
  },

  // Update team
  updateTeam: async (teamId: string, data: UpdateTeamRequest): Promise<Team> => {
    const response = await apiClient.patch<Team>(`/t/${teamId}/settings`, data)
    return response.data
  },

  // Delete team
  deleteTeam: async (teamId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/t/${teamId}/settings`)
    return response.data
  },

  // Transfer ownership (creates pending transfer request)
  transferOwnership: async (
    teamId: string,
    data: TransferOwnershipRequest
  ): Promise<TransferOwnershipResponse> => {
    const response = await apiClient.post<TransferOwnershipResponse>(`/t/${teamId}/transfer`, data)
    return response.data
  },

  // Get pending ownership transfer
  getPendingTransfer: async (teamId: string): Promise<PendingTransferResponse | null> => {
    const response = await apiClient.get<PendingTransferResponse | null>(
      `/t/${teamId}/transfer/pending`
    )
    return response.data
  },

  // Cancel pending ownership transfer
  cancelPendingTransfer: async (teamId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/t/${teamId}/transfer/pending`)
    return response.data
  },

  // Resend ownership transfer email
  resendTransferEmail: async (teamId: string): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>(`/t/${teamId}/transfer/resend`)
    return response.data
  },

  // Get team members
  getMembers: async (teamId: string): Promise<TeamMembersResponse> => {
    const response = await apiClient.get<TeamMembersResponse>(`/t/${teamId}/members`)
    return response.data
  },

  // Update member role
  updateMemberRole: async (
    teamId: string,
    userId: string,
    data: UpdateMemberRoleRequest
  ): Promise<TeamMember> => {
    const response = await apiClient.patch<TeamMember>(`/t/${teamId}/members/${userId}`, data)
    return response.data
  },

  // Remove member
  removeMember: async (teamId: string, userId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/t/${teamId}/members/${userId}`)
    return response.data
  },

  // Leave team
  leaveTeam: async (teamId: string): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>(`/t/${teamId}/leave`)
    return response.data
  },

  // Get pending invites
  getInvites: async (teamId: string): Promise<TeamInvitesResponse> => {
    const response = await apiClient.get<TeamInvitesResponse>(`/t/${teamId}/invites`)
    return response.data
  },

  // Create invite
  createInvite: async (teamId: string, data: CreateInviteRequest): Promise<TeamInvite> => {
    const response = await apiClient.post<TeamInvite>(`/t/${teamId}/invites`, data)
    return response.data
  },

  // Cancel invite
  cancelInvite: async (teamId: string, inviteId: string): Promise<MessageResponse> => {
    const response = await apiClient.delete<MessageResponse>(`/t/${teamId}/invites/${inviteId}`)
    return response.data
  },

  // Resend invite
  resendInvite: async (teamId: string, inviteId: string): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>(
      `/t/${teamId}/invites/${inviteId}/resend`
    )
    return response.data
  },

  // Get invite details (public, no auth required)
  getInviteDetails: async (token: string): Promise<InviteDetails> => {
    const response = await apiClient.get<InviteDetails>(`/invites/${token}`)
    return response.data
  },

  // Accept invite
  acceptInvite: async (token: string): Promise<{ team_id: string }> => {
    const response = await apiClient.post<{ team_id: string }>(`/invites/${token}/accept`)
    return response.data
  },

  // Accept ownership transfer (public, requires token)
  acceptOwnershipTransfer: async (token: string): Promise<AcceptTransferResponse> => {
    const response = await apiClient.post<AcceptTransferResponse>('/transfer/accept', { token })
    return response.data
  },
}
